/**
 * 正規分布と関連する特殊関数。
 *
 * 検定の p 値や信頼区間の臨界値はこのファイルの精度に依存するため、
 * 素朴な近似式ではなく倍精度に近い精度を持つアルゴリズムを使う。
 */

const SQRT2 = Math.SQRT2
const SQRT_2PI = Math.sqrt(2 * Math.PI)

/**
 * 相補誤差関数 erfc(x)。
 * Numerical Recipes 3rd ed. の Chebyshev 近似 (erfccheb)。
 * 相対誤差は倍精度の丸め誤差程度。
 */
const ERFC_COEF = [
  -1.3026537197817094, 6.419697923564902e-1, 1.9476473204185836e-2,
  -9.561514786808631e-3, -9.46595344482036e-4, 3.66839497852761e-4,
  4.2523324806907e-5, -2.0278578112534e-5, -1.624290004647e-6,
  1.30365583558e-6, 1.5626441722e-8, -8.5238095915e-8,
  6.529054439e-9, 5.059343495e-9, -9.91364156e-10,
  -2.27365122e-10, 9.6467911e-11, 2.394038e-12,
  -6.886027e-12, 8.94487e-13, 3.13092e-13,
  -1.12708e-13, 3.81e-16, 7.106e-15,
]

export function erfc(x: number): number {
  const z = Math.abs(x)
  const t = 2 / (2 + z)
  const ty = 4 * t - 2
  let d = 0
  let dd = 0
  for (let j = ERFC_COEF.length - 1; j > 0; j--) {
    const tmp = d
    d = ty * d - dd + ERFC_COEF[j]
    dd = tmp
  }
  const value = t * Math.exp(-z * z + 0.5 * (ERFC_COEF[0] + ty * d) - dd)
  return x >= 0 ? value : 2 - value
}

export function erf(x: number): number {
  return 1 - erfc(x)
}

/** 標準正規分布の累積分布関数 P(Z <= z)。 */
export function normalCdf(z: number): number {
  return 0.5 * erfc(-z / SQRT2)
}

/**
 * 標準正規分布の上側確率 P(Z > z)。
 * 1 - normalCdf(z) と数学的には等価だが、右裾で桁落ちしないためこちらを使う。
 * z = 10 のような値でも有効数字を保てる。
 */
export function normalSf(z: number): number {
  return 0.5 * erfc(z / SQRT2)
}

/** 標準正規分布の確率密度関数。 */
export function normalPdf(z: number): number {
  return Math.exp(-0.5 * z * z) / SQRT_2PI
}

// Acklam の逆正規累積分布関数の有理近似の係数
const A = [
  -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2,
  1.38357751867269e2, -3.066479806614716e1, 2.506628277459239,
]
const B = [
  -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2,
  6.680131188771972e1, -1.328068155288572e1,
]
const C = [
  -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838,
  -2.549732539343734, 4.374664141464968, 2.938163982698783,
]
const D = [
  7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996,
  3.754408661907416,
]

const P_LOW = 0.02425
const P_HIGH = 1 - P_LOW

/**
 * 標準正規分布の分位点関数（逆累積分布関数）。
 * Acklam の有理近似に Halley 法による 1 回の補正を加え、倍精度まで詰めている。
 *
 * @param p 0 < p < 1 の確率
 */
export function normalQuantile(p: number): number {
  if (!Number.isFinite(p) || p <= 0 || p >= 1) {
    if (p === 0) return -Infinity
    if (p === 1) return Infinity
    return NaN
  }

  let x: number
  if (p < P_LOW) {
    const q = Math.sqrt(-2 * Math.log(p))
    x =
      (((((C[0] * q + C[1]) * q + C[2]) * q + C[3]) * q + C[4]) * q + C[5]) /
      ((((D[0] * q + D[1]) * q + D[2]) * q + D[3]) * q + 1)
  } else if (p <= P_HIGH) {
    const q = p - 0.5
    const r = q * q
    x =
      ((((((A[0] * r + A[1]) * r + A[2]) * r + A[3]) * r + A[4]) * r + A[5]) *
        q) /
      (((((B[0] * r + B[1]) * r + B[2]) * r + B[3]) * r + B[4]) * r + 1)
  } else {
    const q = Math.sqrt(-2 * Math.log(1 - p))
    x =
      -(((((C[0] * q + C[1]) * q + C[2]) * q + C[3]) * q + C[4]) * q + C[5]) /
      ((((D[0] * q + D[1]) * q + D[2]) * q + D[3]) * q + 1)
  }

  // Halley 法による補正。erfc が高精度なので 1 回で十分収束する。
  const e = normalCdf(x) - p
  const u = e * SQRT_2PI * Math.exp(0.5 * x * x)
  return x - u / (1 + (x * u) / 2)
}
